import { creditPeriodSummaryKey, } from "@buildwithflux/models";
import { silentLogger, timeout } from "@buildwithflux/shared";
import { metadataStrategyOverwrite } from "../../metadata";
/**
 * Container class for Firebase database access.  Encapsulates all access to documents and collections
 * in such a way that call sites can use the admin or regular firebase client interchangeably.
 *
 * Abstract storage adapter for admin and non-admin firebase clients.  Callers can use these classes
 * to reuse storage logic between admin and non-admin clients.
 *
 * Note that due to some weaknesses in the firebase client API design, there is duplicative code in
 * the subclasses for access collections.  This is unavoidable (I think) due to the way that Google
 * implemented the API in terms of namespaces.
 *
 * It should be an ACTIVE project to see if we can in fact make the types totally generic!
 *
 * @remarks It is important that all read access to database entries that happen via this class only operate
 *          at the level of _references_.
 */
export class FirestoreAdapter {
    db;
    metadataGenerator;
    converters;
    logger;
    /**
     * Underlying database client.
     */
    constructor(db, metadataGenerator, converters, logger = silentLogger) {
        this.db = db;
        this.metadataGenerator = metadataGenerator;
        this.converters = converters;
        this.logger = logger;
    }
    creditAccountSettings(accountUid) {
        return this.creditAccountSettingsCollection().doc(accountUid);
    }
    creditLedgerEntry(accountUid, period, transactionId) {
        return this.creditLedgerEntryCollection(accountUid, period).doc(transactionId);
    }
    creditPeriodSummary(accountUid, period) {
        return this.creditPeriodSummaryCollection().doc(creditPeriodSummaryKey(accountUid, period));
    }
    /**
     * Get a reference to a specific document.
     * @param documentUid The id of the document to retrieve
     * @param metadataWriteStrategy The strategy to use when updaing the associated metadata for this ref
     */
    document(documentUid, metadataWriteStrategy = metadataStrategyOverwrite("baseDocument")) {
        return this.documentCollection(metadataWriteStrategy).doc(documentUid);
    }
    /**
     * Get a reference to a specific comment in a document
     * @param documentUid The id of the document the comment is within
     * @param commentUid The id of the comment
     */
    documentComment(documentUid, commentUid) {
        return this.documentCommentsCollection(documentUid).doc(commentUid);
    }
    /**
     * Get a reference to a specific comment thread in a document
     * @param documentUid
     * @param threadUid
     */
    documentCommentThread(documentUid, threadUid) {
        return this.documentCommentThreadsCollection(documentUid).doc(threadUid);
    }
    /**
     * Get a reference to a specific comment like in a document
     */
    documentCommentLike(documentUid, commentUid, likeOwnerUserUid) {
        return this.documentCommentLikesCollection(documentUid, commentUid).doc(likeOwnerUserUid);
    }
    /**
     * Get a reference to a specific enterprise by EnterpriseUid
     */
    enterprise(enterpriseUid) {
        return this.enterpriseCollection().doc(enterpriseUid);
    }
    /**
     * Get a reference to a specific starred document.
     * @param starredDocumentUid The compound ID of the starred document to retrieve - this is `${documentUid}:${ownerUid}`
     */
    starredDocumentData(starredDocumentUid) {
        return this.starredDocumentCollection().doc(starredDocumentUid);
    }
    /**
     * Get a reference to a specific account follow object.
     */
    accountFollowersData(uid) {
        return this.accountFollowersCollection().doc(uid);
    }
    /**
     * Get a reference to a specific Firestore lease record
     */
    lease(key) {
        return this.leasesCollection().doc(key);
    }
    /**
     * Get a reference to a specific part.
     * @param partUid The part to retrieve
     */
    part(partUid) {
        return this.partCollection().doc(partUid);
    }
    /**
     * Get a reference to a specific part version document.
     * @param partUid The uid of the part to retrieve the version information for.
     * @param partVersionName Name of the part version in question.
     * @returns Deserialized part version data.
     */
    partVersion(partUid, partVersionName) {
        return this.partVersionCollection(partUid).doc(partVersionName);
    }
    /**
     * Get a reference to a specific suggestion.
     */
    suggestion(documentUid, suggestionUid) {
        return this.documentSuggestionsCollection(documentUid).doc(suggestionUid);
    }
    /**
     * Get a reference to a specific markdown conversion.
     */
    markdownConversion(conversionUid) {
        return this.markdownConversionsCollection().doc(conversionUid);
    }
    /**
     * Get a reference to a specific datasheet vectorization.
     */
    datasheetVectorization(vectorizationId) {
        return this.datasheetVectorizationsCollection().doc(vectorizationId);
    }
    documentDatasheetMetadata(documentUid) {
        return this.documentDatasheetMetadataCollection().doc(documentUid);
    }
    /**
     * Forces a connection attempt to Firestore and makes sure the connection is live and working
     *
     * Internally, makes a small get request for a non-existent path
     *
     * The returned promise is resolved empty if the connection is refreshed, and rejected if there is a problem with
     * the connection, including a timeout
     */
    ping() {
        return timeout(this.db
            .collection("ping")
            .doc("0")
            .get({ source: "server" })
            .then(() => { }), 10000);
    }
    /**
     * Be careful when using this method: it provides raw access to methods that may not have the correct converter attached
     *
     * Reasons we access the underlying connection:
     *  - We need to do something that's only supported by one connection type (like .select() is only for admin connections)
     */
    underlyingConnection() {
        return this.db;
    }
    /**
     * Get a reference to a specific user by handle.
     * @param handle The handle of the user to retrieve
     * @returns Deserialized user data.
     */
    user(handle) {
        return this.userCollection().doc(handle);
    }
    /**
     * Get a reference to a handle mapping by handle.
     * @param handle The handle to retrieve
     */
    handleMapping(handle) {
        return this.handleMappingCollection().doc(handle);
    }
    /**
     * Get a reference to a specific user's private metadata.
     * @param userUid The uid of the user to retrieve for
     * @returns Deserialized private user metadata.
     */
    userPrivateMetadata(userUid) {
        return this.userPrivateMetadataCollection().doc(userUid);
    }
    /**
     * Get a reference to a specific organization
     * @param organizationUid The uid of the organization to retrieve
     */
    organization(organizationUid) {
        return this.organizationCollection().doc(organizationUid);
    }
    /**
     * Get a reference to the membership record of a specific user in a specific organization
     */
    organizationMember(organizationUid, userUid) {
        return this.organizationMembersCollection(organizationUid).doc(userUid);
    }
    organizationPrivateMetadata(organizationUid) {
        return this.organizationPrivateMetadataCollection().doc(organizationUid);
    }
    /**
     * Get a reference to a collection, dynamically specified by the field parameters
     *
     * @param documentUid The uid of the document to look under
     * @param field The field name of the collection to retrieve
     */
    documentSubcollectionForField(documentUid, field) {
        switch (field) {
            case "active_users":
                return this.documentActiveUsersCollection(documentUid);
            case "assets":
                return this.documentAssetsCollection(documentUid);
            case "comment_threads":
                return this.documentCommentThreadsCollection(documentUid);
            case "configs":
                return this.documentConfigsCollection(documentUid);
            case "userUiStates":
                return this.documentUserUiStatesCollection(documentUid);
            case "elements":
                return this.documentElementsCollection(documentUid);
            case "nets":
                return this.documentNetsCollection(documentUid);
            case "pcbLayoutNodes":
                return this.documentPcbLayoutNodesCollection(documentUid);
            case "pcbLayoutRuleSets":
                return this.documentPcbLayoutRuleSetsCollection(documentUid);
            case "properties":
                return this.documentPropertiesCollection(documentUid);
            case "roles":
                return this.documentRolesCollection(documentUid);
            case "routeVertices":
                return this.documentRouteVerticesCollection(documentUid);
            case "routes":
                return this.documentRoutesCollection(documentUid);
            case "invites":
                return this.documentInvitesCollection(documentUid);
            default:
                throw new Error(`Unknown document subcollection field: ${field}`);
        }
    }
    documentSubcollectionForKey(documentUid, field) {
        switch (field) {
            case "actionRecords":
                return this.documentActionRecordsCollection(documentUid);
            case "comments":
                return this.documentCommentsCollection(documentUid);
            default:
                return this.documentSubcollectionForField(documentUid, field);
        }
    }
    /**
     * Get a reference to a specific user's analytics data.
     * @param userUid The uid of the user to retrieve
     * @returns Deserialized user analytics.
     */
    userAnalytics(userUid) {
        return this.userAnalyticsCollection().doc(userUid);
    }
}
//# sourceMappingURL=FirestoreAdapter.js.map